import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    HorizontalFlex,
    ButtonSecondary,
    Button,
} from 'smartsuite-ui';
import { DragDropContext } from 'react-beautiful-dnd';
import ColumnConfigSection from './ColumnConfigSection';
import { addToArray, removeFromArray, moveInArray } from './arrayUtils';

function getInitalViewState(tableConfiguration) {
    return tableConfiguration.reduce(
        (result, column) => {
            if (column.isFixed) {
                result.fixed.push(column);
            } else if (column.isHidden) {
                result.hidden.push(column);
            } else {
                result.visible.push(column);
            }

            return result;
        },
        {
            fixed: [],
            visible: [],
            hidden: [],
        }
    );
}

function getTableConfigurationFromViewState(viewState) {
    return [...viewState.fixed, ...viewState.visible, ...viewState.hidden];
}

export default function ConfigurationModal(props) {
    const [viewState, setViewState] = useState(
        getInitalViewState(props.colunmConfig)
    );

    const defaultColumnState = useMemo(
        () =>
            getTableConfigurationFromViewState(
                getInitalViewState(props.defaultTableConfig)
            ),
        [props.defaultTableConfig]
    );

    function defaultEqualsCurrentTableConfiguration() {
        const currentColumnState =
            getTableConfigurationFromViewState(viewState);
        return defaultColumnState.every(
            (column, index) =>
                column.dataKey === currentColumnState[index].dataKey
        );
    }

    function restoreDefaults() {
        setViewState(getInitalViewState(props.defaultTableConfig));
    }

    function handleDragFromSourceToDestination(
        sourceId,
        sourceIndex,
        destId,
        destIndex
    ) {
        setViewState((prevState) => {
            const sourceList = prevState[sourceId];
            const destinationList = prevState[destId];
            const elementToMove = {
                ...sourceList[sourceIndex],
                isHidden: destId === 'hidden',
                isFixed: destId === 'fixed',
            };

            return {
                ...prevState,
                [sourceId]: removeFromArray(sourceList, sourceIndex),
                [destId]: addToArray(elementToMove, destinationList, destIndex),
            };
        });
    }

    function reorderColumns(listName, sourceIndex, destIndex) {
        setViewState((prevState) => ({
            ...prevState,
            [listName]: moveInArray(
                viewState[listName],
                sourceIndex,
                destIndex
            ),
        }));
    }

    function handleOnDragEnd(result) {
        const { source, destination } = result;

        if (!destination) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            reorderColumns(source.droppableId, source.index, destination.index);
        } else {
            handleDragFromSourceToDestination(
                source.droppableId,
                source.index,
                destination.droppableId,
                destination.index
            );
        }
    }

    return (
        <Modal
            onOverlayClick={props.closeModal}
            optionalClassName="configuration-modal__modal-container"
        >
            <ModalHeader onDismiss={props.closeModal}>
                Table Configuration
            </ModalHeader>
            <ModalBody>
                <DragDropContext
                    onDragEnd={(result) => handleOnDragEnd(result)}
                >
                    <div className="table-config-modal">
                        <div className="table-config-modal__left">
                            <ColumnConfigSection
                                columns={viewState.fixed}
                                droppableId="fixed"
                                title="Fixed Columns"
                            />
                            <ColumnConfigSection
                                columns={viewState.visible}
                                droppableId="visible"
                                title="Visible Columns"
                            />
                        </div>
                        <div className="table-config-modal__right">
                            <ColumnConfigSection
                                columns={viewState.hidden}
                                droppableId="hidden"
                                title="Hidden Columns"
                            />
                        </div>
                    </div>
                </DragDropContext>
            </ModalBody>
            <ModalFooter>
                <HorizontalFlex cssClassModifiers={['space-between']}>
                    <div>
                        <ButtonSecondary
                            disabled={defaultEqualsCurrentTableConfiguration()}
                            onClick={restoreDefaults}
                        >
                            Restore Defaults
                        </ButtonSecondary>
                    </div>
                    <HorizontalFlex>
                        <Button
                            onClick={() => {
                                props.onConfirm(
                                    getTableConfigurationFromViewState(
                                        viewState
                                    )
                                );
                                props.closeModal();
                            }}
                        >
                            OK
                        </Button>
                        <ButtonSecondary onClick={props.closeModal}>
                            Cancel
                        </ButtonSecondary>
                    </HorizontalFlex>
                </HorizontalFlex>
            </ModalFooter>
        </Modal>
    );
}

ConfigurationModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    colunmConfig: PropTypes.instanceOf(Array).isRequired,
    onConfirm: PropTypes.func.isRequired,
    defaultTableConfig: PropTypes.instanceOf(Array).isRequired,
};
